import { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { findAll, create, update, remove } from "@/apis/board-comment";
import {
  BoardCommentListItemDto,
  BoardCommentCreateRequestDto,
  BoardCommentUpdateRequestDto,
} from "@/dto/BoardCommentDto";

interface UseCommentListProps {
  boardId: string;
  admIdntfcId?: string;
  isSuperRootUser?: boolean; // 슈퍼 루트 유저 여부
}

export const useCommentList = ({
  boardId,
  admIdntfcId,
  isSuperRootUser = false,
}: UseCommentListProps) => {
  const [comments, setComments] = useState<BoardCommentListItemDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [commentCount, setCommentCount] = useState<number>(0);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>("");

  // 댓글 목록 조회
  const fetchComments = useCallback(
    async (forceRefresh = false) => {
      if (!boardId) return;

      // 이미 데이터를 로드했고, 강제 새로고침이 아니면 로드하지 않음
      if (dataLoaded && !forceRefresh) return;

      setLoading(true);
      setError(null);

      try {
        const response = await findAll({
          boardId,
          page,
          limit,
        });

        if (response.success) {
          setComments(response.data.list || []);
          setCommentCount(response.data.count || 0);
          setDataLoaded(true); // 데이터 로드 완료 표시
        } else {
          setError("댓글을 불러오는데 실패했습니다.");
        }
      } catch (err) {
        console.error("댓글 목록 조회 에러:", err);
        setError("댓글을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    },
    [boardId, page, limit, dataLoaded]
  );

  // expanded가 변경될 때만 데이터 로드
  useEffect(() => {
    if (expanded && !dataLoaded) {
      fetchComments();
    }
  }, [expanded, dataLoaded, fetchComments]);

  // 페이지 변경시 항상 새로 로드
  useEffect(() => {
    if (expanded && dataLoaded) {
      fetchComments(true);
    }
  }, [page, expanded, dataLoaded, fetchComments]);

  // 댓글 작성
  const handleCreateComment = async () => {
    if (!boardId || !admIdntfcId || !commentText.trim()) {
      return;
    }

    setLoading(true);

    try {
      // 백엔드 DTO에 맞게 데이터 형식 수정
      const data: BoardCommentCreateRequestDto = {
        boardId: Number(boardId),
        commentContent: commentText,
        admIdntfcId: admIdntfcId,
      };

      // 대댓글인 경우에만 parentCommentId 추가
      if (replyToId) {
        data.parentCommentId = Number(replyToId);
      }

      console.log("댓글 생성 요청 데이터:", data);

      const response = await create(data);

      if (response.success) {
        // 댓글 목록 다시 조회
        fetchComments(true);
        // 답글 작성 상태 초기화
        setReplyToId(null);
        // 입력창 비우기
        setCommentText("");
      } else {
        setError("댓글 작성에 실패했습니다.");
      }
    } catch (err) {
      console.error("댓글 작성 에러:", err);
      setError("댓글 작성에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 댓글 수정
  const handleUpdateComment = async (commentId: string) => {
    if (!commentId || !commentText.trim()) {
      return;
    }

    setLoading(true);

    try {
      const data: BoardCommentUpdateRequestDto = {
        commentId,
        commentContent: commentText,
      };

      const response = await update(commentId, data);

      if (response.success) {
        // 댓글 목록 다시 조회
        fetchComments(true);
        // 수정 상태 초기화
        setEditingCommentId(null);
        // 입력창 비우기
        setCommentText("");
      } else {
        setError("댓글 수정에 실패했습니다.");
      }
    } catch (err) {
      console.error("댓글 수정 에러:", err);
      setError("댓글 수정에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId: string) => {
    if (!commentId) {
      return;
    }

    if (!window.confirm("댓글을 삭제하시겠습니까?")) {
      return;
    }

    setLoading(true);

    try {
      const response = await remove(commentId);

      if (response.success) {
        // 댓글 목록 다시 조회
        fetchComments(true);
      } else {
        setError("댓글 삭제에 실패했습니다.");
      }
    } catch (err) {
      console.error("댓글 삭제 에러:", err);
      setError("댓글 삭제에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 페이지 변경
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // 댓글 영역 접고 펼치기
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // 답글 작성 모드 설정
  const setReplyMode = (commentId: string | null) => {
    setReplyToId(commentId);
    setEditingCommentId(null);
    setCommentText(""); // 입력창 비우기
  };

  // 댓글 수정 모드 설정
  const setEditMode = (commentId: string | null) => {
    setEditingCommentId(commentId);
    setReplyToId(null);

    if (commentId) {
      // 수정할 댓글의 내용을 입력창에 설정
      const commentToEdit = comments.find((c) => c.commentId === commentId);
      if (commentToEdit) {
        setCommentText(commentToEdit.commentContent);
      }
    } else {
      setCommentText("");
    }
  };

  // 댓글 입력 변경 핸들러
  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommentText(e.target.value);
  };

  // 취소 버튼 핸들러
  const handleCancel = () => {
    setCommentText("");
    setReplyMode(null);
    setEditMode(null);
  };

  // 댓글 제출 핸들러
  const handleSubmitComment = () => {
    if (editingCommentId) {
      handleUpdateComment(editingCommentId);
    } else {
      handleCreateComment();
    }
  };

  // 댓글 새로고침
  const refreshComments = () => {
    fetchComments(true);
  };

  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "yyyy.MM.dd HH:mm", {
        locale: ko,
      });
    } catch (error) {
      return "-";
    }
  };

  // 일반 댓글과 대댓글 분리
  const parentComments = useMemo(() => {
    return comments.filter((comment) => !comment.parentCommentId);
  }, [comments]);

  // 댓글별 대댓글 매핑
  const childCommentMap = useMemo(() => {
    const map: Record<string, BoardCommentListItemDto[]> = {};

    // 1. API에서 직접 제공하는 childComments 처리
    comments.forEach((comment) => {
      if (comment.childComments && comment.childComments.length > 0) {
        map[comment.commentId] = comment.childComments;
      }
    });

    // 2. parentCommentId를 기반으로 대댓글 매핑 (이전 방식 유지)
    comments.forEach((comment) => {
      if (comment.parentCommentId) {
        if (!map[comment.parentCommentId]) {
          map[comment.parentCommentId] = [];
        }
        // 이미 childComments에 포함되어 있지 않은 경우에만 추가
        const alreadyExists = map[comment.parentCommentId].some(
          (child) => child.commentId === comment.commentId
        );
        if (!alreadyExists) {
          map[comment.parentCommentId].push(comment);
        }
      }
    });

    console.log("생성된 대댓글 맵:", map);
    return map;
  }, [comments]);

  // 슈퍼 루트 사용자인지 확인하는 함수
  const isSuperRoot = (commenterAdmIdntfcId: string) => {
    // 슈퍼 루트 유저인 경우 (admIdntfcId가 "0"인 경우)
    return (
      commenterAdmIdntfcId === "0" ||
      (isSuperRootUser && admIdntfcId === commenterAdmIdntfcId)
    );
  };

  return {
    comments,
    parentComments,
    childCommentMap,
    loading,
    error,
    commentCount,
    expanded,
    replyToId,
    editingCommentId,
    page,
    limit,
    commentText,
    fetchComments,
    handleCreateComment,
    handleUpdateComment,
    handleDeleteComment,
    handlePageChange,
    toggleExpanded,
    setReplyMode,
    setEditMode,
    refreshComments,
    formatDate,
    handleCommentChange,
    handleCancel,
    handleSubmitComment,
    isSuperRoot,
  };
};
